import { Dialog } from './Dialog';
import { Button } from './Button';
import styles from './ShortcutsModal.module.css';

// WS-4b (C6) — keyboard shortcuts modal. Spec/12 §12.9.
//
// Lists ONLY the globals actually implemented today. Per PROJECT-RULES
// UI rule #20, surfaces that are not wired must not be listed as if
// they exist. Editor-specific keys (Ctrl+S, Ctrl+B, Ctrl+I, …) are
// rendered as an informational section because users encounter them
// when focused inside an editor; this modal is informational, not a
// claim that the global hook owns those keys.

const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl';

interface ShortcutRow {
  combo: string;
  description: string;
}

const GLOBAL_SHORTCUTS: ShortcutRow[] = [
  { combo: `${MOD} + K`, description: 'Open global search' },
  { combo: `${MOD} + /`, description: 'Toggle this shortcuts panel' },
  { combo: `${MOD} + ,`, description: 'Open your profile' },
  { combo: 'Esc', description: 'Close any open dialog / slide-over / dropdown' },
];

// Spec/12.9 navigation chords (Vim-style two-key sequences). Armed by
// pressing G alone outside an editor / form field; the second key
// fires within a 1.5 s timeout (or the chord cancels). Chord
// destinations honour the same path-type visibility the sidebar
// enforces — see `chordTarget` in `headerChordTarget.ts`. So
// C/G/A/I require an active S1000D project, M prefers an active
// HTML project, otherwise the chord falls back to a truthful
// org-level page.
const NAVIGATION_CHORDS: ShortcutRow[] = [
  {
    combo: 'G then D',
    description: 'Go to project Dashboard (active project — else Home)',
  },
  { combo: 'G then C', description: 'Go to CSDB (active S1000D project — else Home)' },
  {
    combo: 'G then G',
    description: 'Go to Graphics & ICN (active S1000D project — else Home)',
  },
  { combo: 'G then P', description: 'Go to Home' },
  {
    combo: 'G then A',
    description: 'Go to Applicability & CIR (active S1000D project — else Home)',
  },
  { combo: 'G then R', description: 'Go to Review' },
  { combo: 'G then M', description: 'Go to HTML Manuals (active HTML project — else Manuals)' },
  { combo: 'G then I', description: 'Go to Import (active S1000D project — else Home)' },
];

const EDITOR_SHORTCUTS: ShortcutRow[] = [
  { combo: `${MOD} + S`, description: 'Save (within S1000D editor)' },
  { combo: `${MOD} + Z`, description: 'Undo' },
  { combo: `${MOD} + Shift + Z`, description: 'Redo' },
  { combo: `${MOD} + B`, description: 'Bold' },
  { combo: `${MOD} + I`, description: 'Italic' },
  { combo: 'Alt + Shift + W', description: 'Insert warning block' },
  { combo: 'Alt + Shift + C', description: 'Insert caution block' },
  { combo: 'Alt + Shift + N', description: 'Insert note block' },
  { combo: 'Alt + Shift + T', description: 'Insert table' },
];

interface Props {
  open: boolean;
  onClose(): void;
}

function ShortcutTable({ rows }: { rows: ShortcutRow[] }): JSX.Element {
  return (
    <table className={styles.shortcutTable}>
      <tbody>
        {rows.map((row) => (
          <tr key={row.combo} className={styles.shortcutRow}>
            <td className={styles.shortcutCombo}>
              {row.combo}
            </td>
            <td className={styles.shortcutDesc}>{row.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ShortcutsModal({ open, onClose }: Props): JSX.Element {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Keyboard shortcuts"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div data-testid="shortcuts-modal">
        <h3 className={styles.sectionHeading}>Global</h3>
        <ShortcutTable rows={GLOBAL_SHORTCUTS} />
        <h3 className={styles.sectionHeadingSpaced}>Navigation chords</h3>
        <p className={styles.sectionHint}>
          Two-key sequences. Press <code>G</code> first, then the destination
          letter within 1.5 seconds. Press <code>Esc</code> or wait for the
          timeout to cancel. Disabled while typing in any form field or the
          S1000D editor.
        </p>
        <ShortcutTable rows={NAVIGATION_CHORDS} />
        <h3 className={styles.sectionHeadingSpaced}>S1000D editor</h3>
        <p className={styles.sectionHint}>
          These keys are owned by the editor when focus is inside a DM editor
          area. The global hook never intercepts them.
        </p>
        <ShortcutTable rows={EDITOR_SHORTCUTS} />
      </div>
    </Dialog>
  );
}
